import { Badge, DataTableColumnHeader } from "@/components/ui";
import { MATTER_LITIGATION_DATE_RESPONSIBLE_FIELD_LABEL } from "@/lib/data/static/matters/litigationStatics";
import { type Matter } from "@/lib/types";
import { formatDate, formatTime } from "@/lib/utils/format";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";

export type LitigationDateTableRow = Matter["litigationDates"][number] & {
  litigationDateTypeLabel: string;
  litigationDateUserLabel: string;
};

const columnHelper = createColumnHelper<LitigationDateTableRow>();

const formatLitigationDateValue = (value?: string | null) => {
  if (!value) {
    return <span className="text-muted-foreground">Not set</span>;
  }

  return formatDate(value);
};

const formatLitigationDeadlineWithTime = (row: LitigationDateTableRow) => {
  const dateLabel = formatLitigationDateValue(row.deadlineDate);
  const timeLabel = formatTime(row.deadlineTime ?? undefined);

  if (!timeLabel || typeof dateLabel !== "string") {
    return dateLabel;
  }

  return `${dateLabel} • ${timeLabel}`;
};

export const litigationDateTableColumns = [
  columnHelper.accessor("litigationDateTypeLabel", {
    id: "Type",
    enableHiding: false,
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: (cell) => cell.getValue() || "-",
  }),
  columnHelper.accessor("confirmation", {
    id: "Status",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: (cell) => {
      const confirmed = cell.getValue();
      return (
        <Badge variant={confirmed ? "success" : "warning"}>
          {confirmed ? "Confirmed" : "Awaiting confirmation"}
        </Badge>
      );
    },
  }),
  columnHelper.accessor("deadlineDate", {
    id: "Deadline",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: (cell) => formatLitigationDeadlineWithTime(cell.row.original),
    sortingFn: "customDateTime",
  }),
  columnHelper.accessor("completedDate", {
    id: "Completed",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: (cell) => formatLitigationDateValue(cell.getValue()),
    sortingFn: "customDateTime",
  }),
  columnHelper.accessor("litigationDateUserLabel", {
    id: MATTER_LITIGATION_DATE_RESPONSIBLE_FIELD_LABEL,
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: (cell) => {
      const label = cell.getValue().trim();
      if (!label) {
        return <span className="text-muted-foreground">—</span>;
      }
      return label;
    },
  }),
  columnHelper.accessor("description", {
    id: "Description",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: (cell) => {
      const description = cell.getValue();
      if (!description) {
        return <span className="text-muted-foreground">No description</span>;
      }

      return <span className="line-clamp-2">{description}</span>;
    },
  }),
] as ColumnDef<LitigationDateTableRow>[];
